import { useOrgContext } from '@/org/contexts/OrgContext'
import useOrg from '@/org/hooks/useOrg'
import {
  Org_Module_Enum,
  Org_Plan_Type_Enum,
  useOrgPlanModulesQuery,
} from '@gql'
import {
  firstPlanWithModule,
  isModuleEnabled,
} from '@rolebase/shared/model/orgPlan'
import { useMemo } from 'react'

export interface PlanModules {
  /** Le module est-il ouvert pour cette organisation ? */
  isEnabled(module: Org_Module_Enum): boolean
  /** La première formule achetable qui l'ouvre, pour le dire à l'écran. */
  unlockedBy(module: Org_Module_Enum): Org_Plan_Type_Enum | undefined
  /** L'organisation est-elle désactivée ? Alors plus rien n'est ouvert. */
  isOrgDisabled: boolean
}

/**
 * Ce que le forfait de l'organisation courante ouvre.
 *
 * 🔑 **La règle est lue EN BASE** (`org_plan_module`), plus dans une constante
 * TypeScript (ADM06-D5). C'est la même table que traversent les permissions
 * Hasura des dix tables verrouillables : une seule source, consultée des deux
 * côtés. La recopier dans le code aurait garanti qu'un jour l'écran promette ce
 * que le serveur refuse.
 *
 * 🔴 **Deux règles se superposent, et la désactivation l'emporte.** Une
 * organisation désactivée voit tout se fermer, quel que soit son forfait : c'est
 * le levier commercial d'ADM-D2, il n'aurait aucun poids si un forfait payé
 * continuait d'ouvrir des modules. L'ordre est donc : désactivée d'abord,
 * forfait ensuite.
 *
 * 🔴 **En cas d'échec de la requête, on FERME.** Un tableau de règles vide rend
 * `isModuleEnabled` faux partout — c'est voulu, et un test le verrouille. Un
 * défaut de réseau ne doit pas déverrouiller des modules payants ; un écran trop
 * prudent se répare d'un rechargement, une fuite ne se répare pas.
 *
 * ⚠️ Tant que l'organisation n'est pas chargée, tout est considéré comme
 * **ouvert** : une page qui se monte avant ses données ne doit pas clignoter en
 * « verrouillé », et le serveur reste l'autorité de toute façon. La nuance avec
 * le point précédent : ici on ne sait pas encore, là on sait qu'on n'a rien.
 */
export default function usePlanModules(): PlanModules {
  const { orgId } = useOrgContext()
  const org = useOrg(orgId)

  // Données de référence : douze lignes au plus, qui ne changent qu'à la main.
  const { data } = useOrgPlanModulesQuery({
    fetchPolicy: 'cache-first',
  })

  return useMemo(() => {
    const rules = data?.org_plan_module ?? []
    const isOrgDisabled = !!org?.disabledAt

    return {
      isOrgDisabled,

      isEnabled(module: Org_Module_Enum) {
        if (!org) return true
        if (isOrgDisabled) return false
        return isModuleEnabled(rules, org.plan, module)
      },

      unlockedBy(module: Org_Module_Enum) {
        return firstPlanWithModule(rules, module)
      },
    }
  }, [org, data])
}
